import { Box, Breadcrumbs, useMediaQuery } from "@mui/material";
import { IconButton, Typography } from "@mui/material";
import { TeamManagementProps } from "./types";
import { styles } from "./styles";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  DeleteEnterpriseMemberResponse,
  EnterpriseDetails,
  GetInvitationPendingUsersForEnterpriseResponse,
  Role,
} from "../../proto/api_pb";
import { GetEnterpriseAllUsersResponse, User } from "../../proto/api_pb";
import {
  deleteEnterpriseMember,
  getEnterprisePendingInvitation,
} from "../../services/enterpriseService";
import { getEnterpriseAllUsers } from "../../services/enterpriseService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { UserDetailsContext } from "../../contexts";
import { MobileDrawer } from "../../components/MobileDrawer/MobileDrawer";
import { AddEnterpriseMemberModal } from "../../components/Modal/AddEnterpriseMemberModal";
import { toast } from "react-toastify";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import DeleteConfirmationModal from "../../components/Modal/DeleteConfirmationModal";
import { TeamManagementTable } from "../../components/TeamManagementTable/TeamManagementTable";

export const TeamManagement: React.FC<TeamManagementProps> = ({
  currentUser,
}) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const { getValidAccessToken } = useGetValidAccessToken();
  const [selectedEmailToDelete, setSelectedEmailToDelete] =
    useState<string>("");
  const { enterprise } = useContext(UserDetailsContext);

  const getTeamMembers = useCallback(async () => {
    setIsLoading(true);
    const token = await getValidAccessToken();
    const res = await getEnterpriseAllUsers(token, enterprise?.id ?? "");
    if (res instanceof GetEnterpriseAllUsersResponse) {
      setTeamMembers((prevMembers) => [...prevMembers, ...res.getUsersList()]);
    }
    setIsLoading(false);
  }, [enterprise?.id, getValidAccessToken]);

  const handleGetPendingInvitations = useCallback(async () => {
    const token = await getValidAccessToken();
    const res = await getEnterprisePendingInvitation(
      token,
      enterprise?.id ?? ""
    );
    if (res instanceof GetInvitationPendingUsersForEnterpriseResponse) {
      const allInvitedMembers: User[] = [];
      res.getEnterpriseInvitationsList().forEach((invitation) => {
        const newUser = new User();
        newUser.setEmail(invitation.getEmail());
        // newUser.setUsername("dummy");
        const nE = new EnterpriseDetails();
        nE.setEnterpriseId(invitation.getEnterpriseId());
        nE.setDateAdded(invitation.getDateAdded());
        nE.setEnterpriseName(invitation.getEnterpriseName());
        nE.setRole(invitation.getRole());
        newUser.setEnterpriseDetailsList([nE]);
        allInvitedMembers.push(newUser);
      });
      setTeamMembers((prevMembers) => [...prevMembers, ...allInvitedMembers]);
    }
  }, [enterprise?.id, getValidAccessToken]);

  const handleUseEffect = useCallback(async () => {
    await getTeamMembers().then(() => {
      handleGetPendingInvitations();
    });
  }, [getTeamMembers, handleGetPendingInvitations]);

  useEffect(() => {
    handleUseEffect();
  }, [getTeamMembers, handleGetPendingInvitations, handleUseEffect]);

  const handleDeleteMember = async (email: string) => {
    let adminCount = 0;
    teamMembers.forEach((user) => {
      const enterpriseDetails = user
        .getEnterpriseDetailsList()
        .find((e) => e.getEnterpriseId() === enterprise?.id);
      if (
        enterpriseDetails &&
        Number(enterpriseDetails.getRole()) === Role.ROLE_ADMIN
      ) {
        adminCount++;
      }
    });

    const memberToDelete = teamMembers.find(
      (user) => user.getEmail() === email
    );
    const enterpriseDetailsToDelete = memberToDelete
      ?.getEnterpriseDetailsList()
      .find((e) => e.getEnterpriseId() === enterprise?.id);

    if (
      Number(enterpriseDetailsToDelete?.getRole()) !== Role.ROLE_ADMIN ||
      adminCount > 1
    ) {
      const token = await getValidAccessToken();
      const res = await deleteEnterpriseMember(
        token,
        email,
        enterprise?.id ?? ""
      );
      if (res instanceof DeleteEnterpriseMemberResponse) {
        setIsConfirmationModalOpen(false);
        toast.info("Member deleted successfully");
        setTeamMembers([]);
        await handleUseEffect();
      } else {
        console.log(res);
      }
    } else {
      toast.warn("Last admin cannot be deleted");
    }
  };

  const breadcrumbs = [
    <HomeOutlinedIcon key="1" />,
    <IconButton style={styles.breadcrumbsButton} sx={styles.button} key="2">
      Team Management
    </IconButton>,
  ];

  return (
    <>
      {isConfirmationModalOpen && (
        <DeleteConfirmationModal
          isMessageModalOpen={isConfirmationModalOpen}
          message="Are you sure you want to delete the member ?"
          toggle={() => setIsConfirmationModalOpen((prev) => !prev)}
          handleConfirm={() => {
            handleDeleteMember(selectedEmailToDelete);
          }}
        />
      )}
      <MobileDrawer />
      <Box
        sx={{
          padding: "0px var(--container-padding-desktop, 32px)",
          width: "100%",
        }}
      >
        <Box sx={{ margin: "20px 0px 20px 0px" }}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            {breadcrumbs}
          </Breadcrumbs>
        </Box>
        <Box sx={styles.headingContainer}>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", color: "white" }}
            >
              Team members
            </Typography>
            <Typography>
              Manage your team members and their account permissions here.
            </Typography>
          </Box>
          <Box sx={styles.addMembersContainer}>
            {Number(enterprise?.role) !== Role.ROLE_ARTIST && (
              <AddEnterpriseMemberModal
                getTeamMembers={handleUseEffect}
                teamMembers={teamMembers}
                setTeamMembers={setTeamMembers}
              />
            )}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          width: isSmallScreen ? "100vw" : "100%",
          padding: "0px var(--container-padding-desktop, 32px)",
        }}
      >
        <TeamManagementTable
          teamMembers={teamMembers}
          setSelectedEmailToDelete={setSelectedEmailToDelete}
          setIsConfirmationModalOpen={setIsConfirmationModalOpen}
          getTeamMembers={handleUseEffect}
          currentUser={currentUser}
          isLoading={isLoading}
          setTeamMembers={setTeamMembers}
        />
      </Box>
    </>
  );
};
